import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Typography,
  Button,
  Snackbar,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Box,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Delete as DeleteIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import ThemeBackground from '../../components/Layout/ThemeBackground';
import { fetchPublicLookups } from '../../api/lookups';
import { Lookup } from '../../types/lookup';
import AddressAutocomplete from '../../components/common/AddressAutocomplete';
import { EmailField, PhoneField } from '../../components/common/ValidatedFields';
import api from '../../utils/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../index.css';

const steps = ['Personal Information', 'Assessment Questions', 'Review & Submit'];

const genderOptions = ['Male', 'Female', 'Non-binary', 'Prefer not to say', 'Other'];

const months = [
  { value: '01', label: 'January' }, { value: '02', label: 'February' },
  { value: '03', label: 'March' }, { value: '04', label: 'April' },
  { value: '05', label: 'May' }, { value: '06', label: 'June' },
  { value: '07', label: 'July' }, { value: '08', label: 'August' },
  { value: '09', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' }
];

const currentYear = new Date().getFullYear();
const minAge = 18; // Clients must be at least 18 years old
const years = Array.from({ length: 100 }, (_, i) => (currentYear - minAge) - i);

interface EmergencyContact {
  name: string;
  relationship: string;
  phone: string;
}

interface FormData {
  email: string;
  dob: string;
  gender: string;
  street: string;
  addressLine2: string;
  addressLine3: string;
  city: string;
  county: string;
  postcode: string;
  country: string;
  gpPractice: string;
  gpAddress: string;
  gpAddressLine2: string;
  gpAddressLine3: string;
  gpCity: string;
  gpCounty: string;
  gpPostcode: string;
  gpCountry: string;
  gpPhone: string;
  emergencyContacts: EmergencyContact[];
  concernsDuration: string;
  mainObjectives: string;
  personalPreferences: string;
  generalConcerns: string;
  previousCounsellor: string;
  officialDiagnoses: string;
  medication: string;
  recreationalDrugs: string;
  alcoholUse: string;
  substanceSupport: string;
  personalityDisorder: string;
  otherSupport: string;
  sessionTypes: string[];
  availability: string[];
  additionalInfo: string;
  previousSessions: number;
}

interface PreviousSessionDetail {
  sessionDate: string;
  attended: boolean;
  notes: string;
}

interface Errors {
  [key: string]: string;
}

const ExistingClientOnboarding: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCounsellor = user?.role === 'counsellor';
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' | 'warning' });
  const [previousSessionDetails, setPreviousSessionDetails] = useState<PreviousSessionDetail[]>([]);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [tempDate, setTempDate] = useState<Date | null>(null);
  const [currentSessionIndex, setCurrentSessionIndex] = useState<number>(0);
  
  const [clientInfo, setClientInfo] = useState<any>(null);
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  
  const [sessionTypesLookups, setSessionTypesLookups] = useState<Lookup[]>([]);
  const [availabilityLookups, setAvailabilityLookups] = useState<Lookup[]>([]);

  const [formData, setFormData] = useState<FormData>({
    email: '',
    dob: '',
    gender: '',
    street: '',
    addressLine2: '',
    addressLine3: '',
    city: '',
    county: '',
    postcode: '',
    country: '',
    gpPractice: '',
    gpAddress: '',
    gpAddressLine2: '',
    gpAddressLine3: '',
    gpCity: '',
    gpCounty: '',
    gpPostcode: '',
    gpCountry: '',
    gpPhone: '',
    emergencyContacts: [{ name: '', relationship: '', phone: '' }],
    concernsDuration: '',
    mainObjectives: '',
    personalPreferences: '',
    generalConcerns: '',
    previousCounsellor: '',
    officialDiagnoses: '',
    medication: '',
    recreationalDrugs: '',
    alcoholUse: '',
    substanceSupport: '',
    personalityDisorder: '',
    otherSupport: '',
    sessionTypes: [],
    availability: [],
    additionalInfo: '',
    previousSessions: 0
  });

  const [errors, setErrors] = useState<Errors>({});

  // Validation functions
  const validateEmail = (email: string): boolean => {
    // More comprehensive email validation
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(email.trim());
  };

  const validateAge = (dob: string): { isValid: boolean; age?: number; error?: string } => {
    if (!dob) return { isValid: false, error: 'Date of birth is required' };
    
    const birthDate = new Date(dob);
    const today = new Date();
    
    // Check if date is valid
    if (isNaN(birthDate.getTime())) {
      return { isValid: false, error: 'Invalid date of birth' };
    }
    
    // Check if date is not in the future
    if (birthDate > today) {
      return { isValid: false, error: 'Date of birth cannot be in the future' };
    }
    
    // Calculate age
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    // Check minimum age requirement
    if (age < 18) {
      return { isValid: false, age, error: 'Client must be at least 18 years old' };
    }
    
    // Check reasonable maximum age (e.g., 120 years)
    if (age > 120) {
      return { isValid: false, age, error: 'Please enter a valid date of birth' };
    }
    
    return { isValid: true, age };
  };

  const validatePhone = (phone: string): boolean => {
    // UK mobile: 07xxx or +44 7xxx
    // UK landline: 01xxx, 02xxx, 03xxx or +44 1xxx, +44 2xxx, +44 3xxx
    const phoneRegex = /^(\+44\s?[1-3,7-9]\d{2,3}|\(?0[1-3,7-9]\d{2,3}\)?)\s?\d{3,4}\s?\d{3,4}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const validatePostcode = (postcode: string): boolean => {
    const postcodeRegex = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i;
    return postcodeRegex.test(postcode.trim());
  };

  // Initialize session details array when number of previous sessions changes
  useEffect(() => {
    const count = formData.previousSessions || 0;
    const currentCount = previousSessionDetails.length;
    
    if (count > currentCount) {
      // Add new empty sessions
      const newSessions = Array(count - currentCount).fill(null).map(() => ({
        sessionDate: '',
        attended: true,
        notes: ''
      }));
      setPreviousSessionDetails([...previousSessionDetails, ...newSessions]);
    } else if (count < currentCount) {
      // Remove excess sessions
      setPreviousSessionDetails(previousSessionDetails.slice(0, count));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.previousSessions]);

  useEffect(() => {
    const loadClientInfo = async () => {
      try {
        const response = await api.get(`/api/clients/${clientId}`);
        
        // If counsellor and client is already onboarded, redirect to dashboard
        if (isCounsellor && response.data.existingClientOnboarded) {
          setSnackbar({
            open: true,
            message: 'This client has already completed onboarding',
            severity: 'info'
          });
          setTimeout(() => navigate('/counsellor-dashboard'), 2000);
          return;
        }
        
        setClientInfo(response.data);
        setFormData(prev => ({
          ...prev,
          email: response.data.email || ''
        }));
        setLoading(false);
      } catch (error: any) {
        setApiError(error.response?.data?.error || 'Failed to load client information');
        setLoading(false);
      }
    };

    if (clientId) {
      loadClientInfo();
    }
  }, [clientId, isCounsellor, navigate]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [sessionTypes, availability] = await Promise.all([
          fetchPublicLookups('sessionTypes'),
          fetchPublicLookups('availability')
        ]);
        setSessionTypesLookups(sessionTypes);
        setAvailabilityLookups(availability);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleDobChange = (field: 'day' | 'month' | 'year', value: string) => {
    if (field === 'day') setDay(value);
    if (field === 'month') setMonth(value);
    if (field === 'year') setYear(value);
    
    let newDay = field === 'day' ? value : day;
    let newMonth = field === 'month' ? value : month;
    let newYear = field === 'year' ? value : year;
    
    if (newDay && newMonth && newYear) {
      const dob = `${newYear}-${newMonth}-${newDay}`;
      handleChange('dob', dob);
    }
  };

  const updateEmergencyContact = (index: number, field: keyof EmergencyContact, value: string) => {
    const updated = [...formData.emergencyContacts];
    updated[index] = { ...updated[index], [field]: value };
    handleChange('emergencyContacts', updated);
  };

  const addEmergencyContact = () => {
    handleChange('emergencyContacts', [...formData.emergencyContacts, { name: '', relationship: '', phone: '' }]);
  };

  const removeEmergencyContact = (index: number) => {
    const updated = formData.emergencyContacts.filter((_, i) => i !== index);
    handleChange('emergencyContacts', updated);
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Errors = {};

    if (step === 0) {
      // Email validation
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!validateEmail(formData.email)) {
        newErrors.email = 'Please enter a valid email address (e.g., name@example.com)';
      }
      
      // Date of birth validation
      if (!day || !month || !year) {
        newErrors.dob = 'Date of birth is required';
      } else if (formData.dob) {
        const ageValidation = validateAge(formData.dob);
        if (!ageValidation.isValid) {
          newErrors.dob = ageValidation.error || 'Invalid date of birth';
        }
      }
      
      if (!formData.gender) newErrors.gender = 'Gender is required';
      if (!formData.street) newErrors.street = 'Street address is required';
      if (!formData.city) newErrors.city = 'City is required';
      
      if (!formData.postcode) {
        newErrors.postcode = 'Postcode is required';
      } else if (!validatePostcode(formData.postcode)) {
        newErrors.postcode = 'Please enter a valid UK postcode';
      }
      
      // Validate emergency contacts - at least one complete contact is required
      const hasCompleteContact = formData.emergencyContacts.some(
        contact => contact.name && contact.relationship && contact.phone
      );
      
      if (!hasCompleteContact) {
        newErrors.emergencyContacts = 'At least one complete emergency contact is required';
      }
      
      // Validate each emergency contact that has any field filled
      formData.emergencyContacts.forEach((contact, index) => {
        const hasAnyField = contact.name || contact.relationship || contact.phone;
        
        if (hasAnyField) {
          if (!contact.name) {
            newErrors[`emergencyContact${index}Name`] = 'Name is required';
          }
          if (!contact.relationship) {
            newErrors[`emergencyContact${index}Relationship`] = 'Relationship is required';
          }
          if (!contact.phone) {
            newErrors[`emergencyContact${index}Phone`] = 'Phone is required';
          } else if (!validatePhone(contact.phone)) {
            newErrors[`emergencyContact${index}Phone`] = 'Please enter a valid UK phone number';
          }
        }
      });
    }

    if (step === 1) {
      if (!formData.concernsDuration) newErrors.concernsDuration = 'This field is required';
      if (formData.sessionTypes.length === 0) newErrors.sessionTypes = 'Please select at least one session type';
      if (formData.availability.length === 0) newErrors.availability = 'Please select at least one availability option';
      
      // Previous sessions is required for counsellors
      if (isCounsellor && (formData.previousSessions === undefined || formData.previousSessions === null)) {
        newErrors.previousSessions = 'Previous sessions is required';
      }
      
      // Validate session details if previousSessions > 0
      if (isCounsellor && formData.previousSessions > 0) {
        previousSessionDetails.forEach((session, index) => {
          if (!session.sessionDate) {
            newErrors[`session${index}Date`] = 'Session date is required';
          }
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    console.log('handleNext called, activeStep:', activeStep);
    console.log('previousSessionDetails:', previousSessionDetails);
    const isValid = validateStep(activeStep);
    console.log('Validation result:', isValid);
    if (isValid) {
      setActiveStep(prev => prev + 1);
    } else {
      console.log('Validation errors:', errors);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;

    try {
      // First, complete the onboarding
      console.log('Starting onboarding submission...');
      await api.put(`/api/clients/${clientId}/existing-client-onboarding`, formData);
      console.log('Onboarding completed successfully');
      
      // Then create session notes for previous sessions if any
      if (isCounsellor && formData.previousSessions > 0 && previousSessionDetails.length > 0) {
        console.log(`Creating ${previousSessionDetails.length} historical session notes...`);
        
        for (let i = 0; i < previousSessionDetails.length; i++) {
          const session = previousSessionDetails[i];
          if (session.sessionDate) {
            try {
              console.log(`Creating session note ${i + 1}:`, {
                sessionDate: session.sessionDate,
                attended: session.attended
              });
              
              await api.post(`/api/sessions/clients/${clientId}/sessions`, {
                sessionDate: session.sessionDate,
                attended: session.attended,
                absenceReason: session.attended ? undefined : 'Not recorded',
                notes: session.attended ? session.notes : 'Client did not attend this session.',
                noteType: 'historical_note' // Historical sessions from existing client onboarding
              });
              
              console.log(`Session note ${i + 1} created successfully`);
            } catch (noteError: any) {
              console.error(`Error creating session note ${i + 1}:`, noteError);
              console.error('Error details:', noteError.response?.data);
              // Continue with other notes even if one fails
            }
          }
        }
        
        console.log('All session notes processed');
      }
      
      setSnackbar({ open: true, message: 'Onboarding completed successfully!', severity: 'success' });
      setTimeout(() => navigate('/counsellor-dashboard'), 2000);
    } catch (error: any) {
      console.error('Error completing existing client onboarding:', {
        error: error.response?.data?.error || error.message,
        clientId,
        formData
      });
      
      const errorMessage = error.response?.data?.error || 'Failed to submit onboarding';
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
    }
  };

  const renderPersonalInfo = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
        Personal Information
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EmailField
            value={formData.email}
            onChange={(value) => handleChange('email', value)}
            externalError={errors.email}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.9)' }}>
            Date of Birth *
          </Typography>
          <Typography variant="caption" sx={{ mb: 1, display: 'block', color: 'rgba(255, 255, 255, 0.6)' }}>
            Client must be at least 18 years old
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <FormControl fullWidth error={!!errors.dob}>
                <Select
                  value={day}
                  onChange={(e) => handleDobChange('day', e.target.value)}
                  displayEmpty
                  className="theme-input"
                  sx={{
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                    '& .MuiSvgIcon-root': { color: 'white' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#1e3a8a',
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                          '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.3)' },
                          '&.Mui-selected:hover': { bgcolor: 'rgba(99, 102, 241, 0.4)' }
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value="">Day</MenuItem>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                    <MenuItem key={d} value={String(d).padStart(2, '0')}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth error={!!errors.dob}>
                <Select
                  value={month}
                  onChange={(e) => handleDobChange('month', e.target.value)}
                  displayEmpty
                  className="theme-input"
                  sx={{
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                    '& .MuiSvgIcon-root': { color: 'white' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#1e3a8a',
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                          '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.3)' },
                          '&.Mui-selected:hover': { bgcolor: 'rgba(99, 102, 241, 0.4)' }
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value="">Month</MenuItem>
                  {months.map(m => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth error={!!errors.dob}>
                <Select
                  value={year}
                  onChange={(e) => handleDobChange('year', e.target.value)}
                  displayEmpty
                  className="theme-input"
                  sx={{
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                    '& .MuiSvgIcon-root': { color: 'white' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#1e3a8a',
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                          '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.3)' },
                          '&.Mui-selected:hover': { bgcolor: 'rgba(99, 102, 241, 0.4)' }
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value="">Year</MenuItem>
                  {years.map(y => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {errors.dob && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {errors.dob}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth error={!!errors.gender}>
            <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Gender</InputLabel>
            <Select
              value={formData.gender}
              onChange={(e) => handleChange('gender', e.target.value)}
              label="Gender"
              className="theme-input"
              sx={{
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                '& .MuiSvgIcon-root': { color: 'white' }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: '#1e3a8a',
                    '& .MuiMenuItem-root': {
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                      '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.3)' },
                      '&.Mui-selected:hover': { bgcolor: 'rgba(99, 102, 241, 0.4)' }
                    }
                  }
                }
              }}
            >
              {genderOptions.map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <AddressAutocomplete
            value={{
              street: formData.street,
              line2: formData.addressLine2,
              line3: formData.addressLine3,
              city: formData.city,
              county: formData.county,
              postcode: formData.postcode,
              country: formData.country
            }}
            onChange={(address) => {
              handleChange('street', address.street || '');
              handleChange('addressLine2', address.line2 || '');
              handleChange('addressLine3', address.line3 || '');
              handleChange('city', address.city || '');
              handleChange('county', address.county || '');
              handleChange('postcode', address.postcode || '');
              handleChange('country', address.country || '');
            }}
            required={true}
            label="Client Address"
            fieldMapping={{
              line1: 'street',
              line2: 'line2',
              line3: 'line3',
              city: 'city',
              postcode: 'postcode',
              county: 'county',
              country: 'country'
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
            GP Information
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="GP Practice Name"
            value={formData.gpPractice}
            onChange={(e) => handleChange('gpPractice', e.target.value)}
            className="theme-input"
            sx={{
              '& .MuiInputBase-input': { color: 'white' },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <PhoneField
            value={formData.gpPhone}
            onChange={(value) => handleChange('gpPhone', value)}
            label="GP Phone Number"
            required={false}
          />
        </Grid>

        <Grid item xs={12}>
          <AddressAutocomplete
            value={{
              line1: formData.gpAddress,
              line2: formData.gpAddressLine2,
              line3: formData.gpAddressLine3,
              city: formData.gpCity,
              county: formData.gpCounty,
              postcode: formData.gpPostcode,
              country: formData.gpCountry
            }}
            onChange={(address) => {
              handleChange('gpAddress', address.line1 || '');
              handleChange('gpAddressLine2', address.line2 || '');
              handleChange('gpAddressLine3', address.line3 || '');
              handleChange('gpCity', address.city || '');
              handleChange('gpCounty', address.county || '');
              handleChange('gpPostcode', address.postcode || '');
              handleChange('gpCountry', address.country || '');
            }}
            required={false}
            label="GP Practice Address"
            fieldMapping={{
              line1: 'line1',
              line2: 'line2',
              line3: 'line3',
              city: 'city',
              postcode: 'postcode',
              county: 'county',
              country: 'country'
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
            Emergency Contacts <span style={{ color: '#f44336' }}>*</span>
          </Typography>
          {errors.emergencyContacts && (
            <Typography variant="caption" sx={{ color: '#f44336', display: 'block', mb: 1 }}>
              {errors.emergencyContacts}
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 2 }}>
            At least one complete emergency contact is required
          </Typography>
        </Grid>

        {formData.emergencyContacts.map((contact, index) => (
          <Grid item xs={12} key={index}>
            <Box sx={{ p: 2, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={contact.name}
                    onChange={(e) => updateEmergencyContact(index, 'name', e.target.value)}
                    error={!!errors[`emergencyContact${index}Name`]}
                    helperText={errors[`emergencyContact${index}Name`]}
                    className="theme-input"
                    sx={{
                      '& .MuiInputBase-input': { color: 'white' },
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                      '& .MuiFormHelperText-root': { color: errors[`emergencyContact${index}Name`] ? '#f44336' : 'rgba(255, 255, 255, 0.7)' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Relationship"
                    value={contact.relationship}
                    onChange={(e) => updateEmergencyContact(index, 'relationship', e.target.value)}
                    error={!!errors[`emergencyContact${index}Relationship`]}
                    helperText={errors[`emergencyContact${index}Relationship`]}
                    className="theme-input"
                    sx={{
                      '& .MuiInputBase-input': { color: 'white' },
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                      '& .MuiFormHelperText-root': { color: errors[`emergencyContact${index}Relationship`] ? '#f44336' : 'rgba(255, 255, 255, 0.7)' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <PhoneField
                    value={contact.phone}
                    onChange={(value) => updateEmergencyContact(index, 'phone', value)}
                    label="Phone"
                    externalError={errors[`emergencyContact${index}Phone`]}
                    required={false}
                  />
                </Grid>
                {formData.emergencyContacts.length > 1 && (
                  <Grid item xs={12} sm={1}>
                    <IconButton onClick={() => removeEmergencyContact(index)} sx={{ color: '#f44336' }}>
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Button
            variant="outlined"
            onClick={addEmergencyContact}
            sx={{
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': { borderColor: 'rgba(255, 255, 255, 0.5)', bgcolor: 'rgba(255, 255, 255, 0.05)' }
            }}
          >
            Add Another Emergency Contact
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  const renderAssessmentQuestions = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
        Assessment Questions
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="How long have you had these concerns?"
            value={formData.concernsDuration}
            onChange={(e) => handleChange('concernsDuration', e.target.value)}
            error={!!errors.concernsDuration}
            helperText={errors.concernsDuration}
            multiline
            rows={2}
            className="theme-input"
            sx={{
              '& .MuiInputBase-input': { color: 'white' },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
              '& .MuiFormHelperText-root': { color: errors.concernsDuration ? '#f44336' : 'rgba(255, 255, 255, 0.7)' }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="What are your main objectives for counselling?"
            value={formData.mainObjectives}
            onChange={(e) => handleChange('mainObjectives', e.target.value)}
            multiline
            rows={2}
            className="theme-input"
            sx={{
              '& .MuiInputBase-input': { color: 'white' },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="What are your general concerns?"
            value={formData.generalConcerns}
            onChange={(e) => handleChange('generalConcerns', e.target.value)}
            multiline
            rows={2}
            className="theme-input"
            sx={{
              '& .MuiInputBase-input': { color: 'white' },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Have you had counselling before? If so, please provide details."
            value={formData.previousCounsellor}
            onChange={(e) => handleChange('previousCounsellor', e.target.value)}
            multiline
            rows={2}
            className="theme-input"
            sx={{
              '& .MuiInputBase-input': { color: 'white' },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Have you been officially diagnosed with any conditions?"
            value={formData.officialDiagnoses}
            onChange={(e) => handleChange('officialDiagnoses', e.target.value)}
            multiline
            rows={2}
            className="theme-input"
            sx={{
              '& .MuiInputBase-input': { color: 'white' },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Are you currently taking any medication?"
            value={formData.medication}
            onChange={(e) => handleChange('medication', e.target.value)}
            multiline
            rows={2}
            className="theme-input"
            sx={{
              '& .MuiInputBase-input': { color: 'white' },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Do you use recreational drugs?"
            value={formData.recreationalDrugs}
            onChange={(e) => handleChange('recreationalDrugs', e.target.value)}
            multiline
            rows={2}
            className="theme-input"
            sx={{
              '& .MuiInputBase-input': { color: 'white' },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Do you consume alcohol? If so, how much?"
            value={formData.alcoholUse}
            onChange={(e) => handleChange('alcoholUse', e.target.value)}
            multiline
            rows={2}
            className="theme-input"
            sx={{
              '& .MuiInputBase-input': { color: 'white' },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Are you receiving support for substance use?"
            value={formData.substanceSupport}
            onChange={(e) => handleChange('substanceSupport', e.target.value)}
            multiline
            rows={2}
            className="theme-input"
            sx={{
              '& .MuiInputBase-input': { color: 'white' },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Have you been diagnosed with a personality disorder?"
            value={formData.personalityDisorder}
            onChange={(e) => handleChange('personalityDisorder', e.target.value)}
            multiline
            rows={2}
            className="theme-input"
            sx={{
              '& .MuiInputBase-input': { color: 'white' },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Are you receiving any other support (e.g., therapy, support groups)?"
            value={formData.otherSupport}
            onChange={(e) => handleChange('otherSupport', e.target.value)}
            multiline
            rows={2}
            className="theme-input"
            sx={{
              '& .MuiInputBase-input': { color: 'white' },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth error={!!errors.sessionTypes}>
            <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Preferred Session Types *</InputLabel>
            <Select
              multiple
              value={formData.sessionTypes}
              onChange={(e) => handleChange('sessionTypes', e.target.value)}
              label="Preferred Session Types *"
              className="theme-input"
              sx={{
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                '& .MuiSvgIcon-root': { color: 'white' }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: '#1e3a8a',
                    '& .MuiMenuItem-root': {
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                      '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.3)' },
                      '&.Mui-selected:hover': { bgcolor: 'rgba(99, 102, 241, 0.4)' }
                    }
                  }
                }
              }}
            >
              {sessionTypesLookups.map((type) => (
                <MenuItem key={type._id} value={type._id}>
                  {type.displayName}
                </MenuItem>
              ))}
            </Select>
            {errors.sessionTypes && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {errors.sessionTypes}
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth error={!!errors.availability}>
            <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Availability *</InputLabel>
            <Select
              multiple
              value={formData.availability}
              onChange={(e) => handleChange('availability', e.target.value)}
              label="Availability *"
              className="theme-input"
              sx={{
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                '& .MuiSvgIcon-root': { color: 'white' }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: '#1e3a8a',
                    '& .MuiMenuItem-root': {
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                      '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.3)' },
                      '&.Mui-selected:hover': { bgcolor: 'rgba(99, 102, 241, 0.4)' }
                    }
                  }
                }
              }}
            >
              {availabilityLookups.map((avail) => (
                <MenuItem key={avail._id} value={avail._id}>
                  {avail.displayName}
                </MenuItem>
              ))}
            </Select>
            {errors.availability && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {errors.availability}
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Additional Information"
            value={formData.additionalInfo}
            onChange={(e) => handleChange('additionalInfo', e.target.value)}
            multiline
            rows={3}
            className="theme-input"
            sx={{
              '& .MuiInputBase-input': { color: 'white' },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' }
            }}
          />
        </Grid>

        {/* Previous Sessions - Only for counsellors */}
        {isCounsellor && (
          <>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                Previous Sessions
      </Typography>
              <Box sx={{ p: 3, backgroundColor: 'rgba(245, 158, 11, 0.1)', borderRadius: 2, mb: 2, border: '1px solid rgba(245, 158, 11, 0.3)' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  Enter the number of sessions this client completed before moving to the digital system. This will count toward your total hours but will not incur any expense.
        </Typography>
      </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Number of Previous Sessions *"
                value={formData.previousSessions}
                onChange={(e) => handleChange('previousSessions', parseInt(e.target.value) || 0)}
                error={!!errors.previousSessions}
                helperText={
                  clientInfo?.existingClientOnboarded 
                    ? 'This field cannot be edited after onboarding is complete'
                    : errors.previousSessions || 'Sessions completed before digital system'
                }
                disabled={clientInfo?.existingClientOnboarded}
                InputProps={{ inputProps: { min: 0 } }}
              className="theme-input"
              sx={{
                  '& .MuiInputBase-input': { color: 'white' },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                  '& .MuiFormHelperText-root': { color: errors.previousSessions ? '#f44336' : 'rgba(255, 255, 255, 0.7)' },
                  '& .Mui-disabled': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    WebkitTextFillColor: 'rgba(255, 255, 255, 0.5)'
                  }
                }}
              />
            </Grid>

            {/* Session Details - Show when previousSessions > 0 */}
            {formData.previousSessions > 0 && (
              <Grid item xs={12}>
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                    Session Details
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)' }}>
                    Please provide details for each previous session below:
                  </Typography>
                  
                  {previousSessionDetails.map((session, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        mb: 3, 
                        p: 3, 
                        backgroundColor: 'rgba(255, 255, 255, 0.03)', 
                        borderRadius: 2,
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'rgba(255, 255, 255, 0.9)' }}>
                        Session {index + 1}
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5, display: 'block' }}>
                              Session Date & Time *
                            </Typography>
                            <button
                              type="button"
                              onClick={() => {
                                if (!clientInfo?.existingClientOnboarded) {
                                  setCurrentSessionIndex(index);
                                  setTempDate(session.sessionDate ? new Date(session.sessionDate) : new Date());
                                  setDatePickerOpen(true);
                                }
                              }}
                              disabled={clientInfo?.existingClientOnboarded}
                              className="mt-1 block w-full p-3 border border-white/20 rounded-lg bg-white/5 text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/50 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {session.sessionDate
                                ? new Date(session.sessionDate).toLocaleString('en-GB', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                : 'Select date and time'}
                            </button>
                            {errors[`session${index}Date`] && (
                              <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
                                {errors[`session${index}Date`]}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Did Client Attend? *</InputLabel>
                            <Select
                              value={session.attended ? 'yes' : 'no'}
                              onChange={(e) => {
                                const updated = [...previousSessionDetails];
                                updated[index].attended = e.target.value === 'yes';
                                setPreviousSessionDetails(updated);
                              }}
                              disabled={clientInfo?.existingClientOnboarded}
                              label="Did Client Attend? *"
                              sx={{
                                color: 'white',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                                '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' }
                              }}
                            >
                              <MenuItem value="yes">Yes</MenuItem>
                              <MenuItem value="no">No</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        
                        {session.attended && (
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              rows={4}
                              label="Session Notes"
                              value={session.notes}
                              onChange={(e) => {
                                const updated = [...previousSessionDetails];
                                updated[index].notes = e.target.value.slice(0, 250);
                                setPreviousSessionDetails(updated);
                              }}
                              disabled={clientInfo?.existingClientOnboarded}
                              placeholder="Brief notes about this session..."
                              helperText={`Session notes should be brief which is why they have been restricted to 250 characters. ${session.notes.length}/250`}
                              className="theme-input"
                              sx={{
                                '& .MuiInputBase-input': { color: 'white' },
                                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                                '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                                '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.9)' }
                              }}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  ))}
                </Box>
              </Grid>
            )}
          </>
        )}
        </Grid>
    </Box>
  );

  const renderReviewSubmit = () => {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
          Review & Submit
            </Typography>

        <Box sx={{ p: 3, backgroundColor: 'rgba(33, 150, 243, 0.1)', borderRadius: 2, mb: 3 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
            Please review the information below before submitting.
                </Typography>
        </Box>

        <Grid container spacing={3}>
              <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)', mb: 2 }}>
              Assessment Information
                </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                <strong>Concerns Duration:</strong> {formData.concernsDuration}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                  <strong>Session Types:</strong> {formData.sessionTypes.map(st => 
                    sessionTypesLookups.find(l => l._id === st)?.displayName || st
                  ).join(', ')}
                </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                <strong>Availability:</strong> {formData.availability.map(av => 
                  availabilityLookups.find(l => l._id === av)?.displayName || av
                ).join(', ')}
                </Typography>
              {isCounsellor && formData.previousSessions > 0 && (
                <>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                    <strong>Previous Sessions:</strong> {formData.previousSessions}
                  </Typography>
                  
                  {previousSessionDetails.length > 0 && (
                    <Box sx={{ mt: 2, pl: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1, fontWeight: 600 }}>
                        Session Details:
                      </Typography>
                      {previousSessionDetails.map((session, index) => (
                        <Box key={index} sx={{ mb: 2, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.03)', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 600 }}>
                            Session {index + 1}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem' }}>
                            Date: {session.sessionDate ? new Date(session.sessionDate).toLocaleString() : 'Not set'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem' }}>
                            Attended: {session.attended ? 'Yes' : 'No'}
                          </Typography>
                          {session.attended && session.notes && (
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem', mt: 0.5 }}>
                              Notes: {session.notes.substring(0, 100)}{session.notes.length > 100 ? '...' : ''}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderPersonalInfo();
      case 1:
        return renderAssessmentQuestions();
      case 2:
        return renderReviewSubmit();
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <ThemeBackground variant="full">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </Box>
      </ThemeBackground>
    );
  }

  if (apiError) {
    return (
      <ThemeBackground variant="full">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <Alert severity="error">{apiError}</Alert>
        </Box>
      </ThemeBackground>
    );
  }

  return (
    <ThemeBackground variant="full">
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton
              onClick={() => navigate('/counsellor-dashboard')}
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                mr: 2,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              aria-label="Back to dashboard"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" className="theme-text-primary" sx={{ fontWeight: 700 }}>
              Existing Client Onboarding
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', ml: 7 }}>
            {clientInfo?.firstName} {clientInfo?.lastName}
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel sx={{ '& .MuiStepLabel-label': { color: 'rgba(255, 255, 255, 0.7)' }, '& .MuiStepLabel-label.Mui-active': { color: 'rgba(255, 255, 255, 1)' } }}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.05)', 
          borderRadius: 2, 
          p: 4,
          minHeight: 400
        }}>
          {renderStepContent(activeStep)}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ 
              color: 'white',
              '&:disabled': { color: 'rgba(255, 255, 255, 0.3)' }
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
            sx={{
              bgcolor: '#6366f1',
              '&:hover': { bgcolor: '#4f46e5' }
            }}
          >
            {activeStep === steps.length - 1 ? 'Complete Onboarding' : 'Next'}
          </Button>
        </Box>
      </Box>

      {/* Date Picker Dialog */}
      <Dialog
        open={datePickerOpen}
        onClose={() => setDatePickerOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" className="theme-text-primary" sx={{ fontWeight: 600 }}>
            Select Session Date & Time
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', pb: 2 }}>
          <DatePicker
            selected={tempDate}
            onChange={(date: Date | null) => setTempDate(date)}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            dateFormat="dd/MM/yyyy HH:mm"
            maxDate={new Date()}
            inline
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setDatePickerOpen(false)}
            sx={{ 
              textTransform: 'none', 
              color: 'rgba(255, 255, 255, 0.6)',
              '&:hover': {
                color: 'rgba(255, 255, 255, 0.8)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (tempDate) {
                const updated = [...previousSessionDetails];
                updated[currentSessionIndex].sessionDate = tempDate.toISOString();
                setPreviousSessionDetails(updated);
              }
              setDatePickerOpen(false);
            }}
            variant="contained"
            sx={{ 
              textTransform: 'none',
              background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)'
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeBackground>
  );
};

export default ExistingClientOnboarding;
